import type { GraphVisualizationFrame } from "@salesko/contracts";

export const sampleText =
  "Met Alice Chen and David Li in Singapore last week. Alice is VP Product at OpenAI, while David and Sam Altman are both tied to the Board Meeting. Alice introduced YC and Tencent investors, and mentioned that Acme Taiwan wants inbound leads auto-organised into their CRM.";

export const defaultGraphFrame = {
  schemaVersion: "graph.visualization.v1",
  frameId: "default-preview",
  graph: {
    nodes: [
      {
        id: "alice-chen",
        type: "person",
        label: "Alice Chen",
        summary: "VP Product at OpenAI; introduced investors and surfaced the Acme Taiwan opportunity.",
        confidence: 0.94,
        evidence: "Alice is VP Product at OpenAI and introduced YC and Tencent investors.",
        visual: {
          position: { x: -0.9, y: 0.34, z: 0.16 },
          color: "#4a8cb0",
          size: 1.18,
          emphasis: "focused"
        },
        data: {
          role: "VP Product · OpenAI"
        }
      },
      {
        id: "david-li",
        type: "person",
        label: "David Li",
        summary: "Contact tied to the board meeting context.",
        confidence: 0.84,
        evidence: "David and Sam Altman are both tied to the board meeting.",
        visual: {
          position: { x: 0.36, y: 0.8, z: -0.12 },
          color: "#4a8cb0",
          size: 0.9,
          emphasis: "normal"
        },
        data: {
          role: "Board meeting contact"
        }
      },
      {
        id: "openai",
        type: "organisation",
        label: "OpenAI",
        summary: "Alice Chen's organisation.",
        confidence: 0.93,
        evidence: "Alice is VP Product at OpenAI.",
        visual: {
          position: { x: -1.18, y: -0.26, z: -0.18 },
          color: "#6ba872",
          size: 1,
          emphasis: "normal"
        },
        data: {
          role: "Company"
        }
      },
      {
        id: "board-meeting",
        type: "event",
        label: "Board Meeting",
        summary: "Relationship event linking David Li and Sam Altman.",
        confidence: 0.78,
        evidence: "David and Sam Altman are both tied to the board meeting.",
        visual: {
          position: { x: 0.8, y: 0.26, z: 0.28 },
          color: "#d9974f",
          size: 0.88,
          emphasis: "normal"
        },
        data: {
          role: "Relationship event"
        }
      },
      {
        id: "sg-meeting",
        type: "event",
        label: "Singapore Meeting",
        summary: "The Singapore catch-up where Alice and David were first captured.",
        confidence: 0.82,
        evidence: "Alice Chen and David Li were met in Singapore last week.",
        visual: {
          position: { x: -0.35, y: 0.5, z: 0.4 },
          color: "#d9974f",
          size: 0.9,
          emphasis: "normal"
        },
        data: {
          role: "Relationship event",
          timeText: "last week"
        }
      },
      {
        id: "yc",
        type: "organisation",
        label: "YC",
        summary: "Investor network introduced by Alice.",
        confidence: 0.82,
        evidence: "Alice introduced investors from YC.",
        visual: {
          position: { x: -0.18, y: -0.92, z: 0.18 },
          color: "#6ba872",
          size: 0.78,
          emphasis: "normal"
        },
        data: {
          role: "Investor network"
        }
      },
      {
        id: "tencent",
        type: "organisation",
        label: "Tencent",
        summary: "Investor network introduced by Alice.",
        confidence: 0.82,
        evidence: "Alice introduced investors from Tencent.",
        visual: {
          position: { x: 0.48, y: -0.72, z: -0.34 },
          color: "#6ba872",
          size: 0.78,
          emphasis: "normal"
        },
        data: {
          role: "Investor network"
        }
      },
      {
        id: "acme-taiwan",
        type: "opportunity",
        label: "Acme Taiwan",
        summary: "Inbound lead auto-intake to CRM opportunity surfaced through Alice.",
        confidence: 0.87,
        evidence: "Acme Taiwan wants inbound leads auto-organised into CRM.",
        visual: {
          position: { x: 1.2, y: -0.28, z: 0.08 },
          color: "#cba94f",
          size: 1.06,
          emphasis: "review"
        },
        data: {
          role: "Opportunity · inbound lead auto-intake"
        }
      },
      {
        id: "singapore-note",
        type: "note",
        label: "Singapore meeting note",
        summary: "Source note behind this default preview frame.",
        confidence: 0.9,
        evidence: sampleText,
        visual: {
          position: { x: -0.2, y: 0.08, z: 0.86 },
          color: "#837a6a",
          size: 0.72,
          emphasis: "normal"
        },
        data: {
          source: "default preview"
        }
      }
    ],
    edges: [
      {
        id: "alice-openai",
        source: "alice-chen",
        target: "openai",
        type: "works_at",
        label: "works at",
        confidence: 0.93,
        evidence: "Alice is VP Product at OpenAI.",
        strength: 0.86,
        data: {}
      },
      {
        id: "alice-yc",
        source: "alice-chen",
        target: "yc",
        type: "introduced_by",
        label: "introduced investor",
        confidence: 0.82,
        evidence: "Alice introduced YC investors.",
        strength: 0.72,
        data: {}
      },
      {
        id: "alice-tencent",
        source: "alice-chen",
        target: "tencent",
        type: "introduced_by",
        label: "introduced investor",
        confidence: 0.82,
        evidence: "Alice introduced Tencent investors.",
        strength: 0.72,
        data: {}
      },
      {
        id: "alice-acme",
        source: "alice-chen",
        target: "acme-taiwan",
        type: "opportunity_at",
        label: "surfaced opportunity",
        confidence: 0.87,
        evidence: "Alice mentioned Acme Taiwan wants inbound lead auto-intake.",
        strength: 0.8,
        data: {}
      },
      {
        id: "david-board",
        source: "david-li",
        target: "board-meeting",
        type: "attended",
        label: "tied to",
        confidence: 0.78,
        evidence: "David is tied to the board meeting.",
        strength: 0.68,
        data: {}
      },
      {
        id: "alice-sg-meeting",
        source: "alice-chen",
        target: "sg-meeting",
        type: "attended",
        label: "met at",
        confidence: 0.86,
        evidence: "Alice Chen and David Li were met in Singapore last week.",
        strength: 0.7,
        data: {}
      },
      {
        id: "david-sg-meeting",
        source: "david-li",
        target: "sg-meeting",
        type: "attended",
        label: "met at",
        confidence: 0.84,
        evidence: "Alice Chen and David Li were met in Singapore last week.",
        strength: 0.68,
        data: {}
      },
      {
        id: "note-alice",
        source: "singapore-note",
        target: "alice-chen",
        type: "mentioned",
        label: "mentions",
        confidence: 0.9,
        evidence: sampleText,
        strength: 0.64,
        data: {}
      },
      {
        id: "note-david",
        source: "singapore-note",
        target: "david-li",
        type: "mentioned",
        label: "mentions",
        confidence: 0.84,
        evidence: sampleText,
        strength: 0.54,
        data: {}
      },
      {
        id: "note-acme",
        source: "singapore-note",
        target: "acme-taiwan",
        type: "mentioned",
        label: "mentions",
        confidence: 0.86,
        evidence: sampleText,
        strength: 0.6,
        data: {}
      }
    ]
  },
  timeline: [
    {
      id: "singapore-meeting",
      label: "Singapore meeting",
      nodeIds: ["alice-chen", "david-li", "singapore-note"],
      summary: "Alice and David were captured from the same relationship note."
    }
  ],
  camera: {
    mode: "flat",
    focusNodeId: "alice-chen",
    position: { x: 0, y: 0, z: 9 },
    orbit: {
      radius: 9,
      autoRotate: false
    }
  },
  ui: {
    productName: "Salesko",
    activeView: "graph",
    searchPlaceholder: "Search people, companies, events...",
    filters: [
      { entityType: "person", enabled: true, color: "#4a8cb0", count: 2 },
      { entityType: "organisation", enabled: true, color: "#6ba872", count: 3 },
      { entityType: "event", enabled: true, color: "#d9974f", count: 2 },
      { entityType: "opportunity", enabled: true, color: "#cba94f", count: 1 },
      { entityType: "note", enabled: true, color: "#837a6a", count: 1 }
    ],
    relationshipStrength: 0.8,
    // Demo wiring: mark the hub of this preview network as the ego-center so the
    // default graph showcases the hero self-node treatment. Real frames populate
    // selfNodeId from authenticated identity (see docs — not a label heuristic).
    selfNodeId: "alice-chen",
    detailPanel: {
      selectedNodeId: "alice-chen",
      title: "Alice Chen",
      subtitle: "VP Product · OpenAI",
      tags: ["default preview", "warm intro", "opportunity source"],
      about: "Alice connects the relationship note to OpenAI, YC, Tencent, and the Acme Taiwan opportunity.",
      linkedNodeIds: ["openai", "yc", "tencent", "acme-taiwan", "singapore-note"]
    },
    stats: {
      people: 2,
      events: 2,
      organisations: 3,
      opportunities: 1
    }
  },
  commands: [],
  facets: {
    entityTypes: ["person", "organisation", "event", "opportunity", "note"],
    relationTypes: ["works_at", "introduced_by", "opportunity_at", "attended", "mentioned"],
    warnings: []
  },
  summary: "Default preview · graph.visualization.v1 · 9 nodes",
  needsReview: false,
  source: "deterministic-rule"
} satisfies GraphVisualizationFrame;
